import React, { useEffect, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Image,
    Spinner,
    Alert,
    Badge,
} from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";

const OffersPage = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});

    // Form fields matching OfferController attributes
    const [title, setTitle] = useState("");
    const [desc, setDesc] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [image, setImage] = useState(null);
    const [preview, setPreview] = useState(null);
    const [currentOfferId, setCurrentOfferId] = useState(null);

    const getAuthHeaders = () => ({
        Authorization: localStorage.getItem("authToken")
            ? `Bearer ${localStorage.getItem("authToken")}`
            : "",
    });

    const getImageUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        return `${process.env.REACT_APP_IMG_URL}/${path}`;
    };

    // Fetch Offer details
    const fetchOffer = async () => {
        try {
            setLoading(true);
            setError("");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/web-manager/offers`,
                { headers: getAuthHeaders() }
            );

            // Handle collection or single object response
            const rawData = response.data?.data || response.data || [];
            const offerData = Array.isArray(rawData) ? rawData[0] : rawData;

            if (offerData) {
                setCurrentOfferId(offerData.id || null);
                setTitle(offerData.title || "");
                setDesc(offerData.desc || "");
                setIsActive(Boolean(offerData.is_active));
                if (offerData.image) {
                    setPreview(getImageUrl(offerData.image));
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load offer settings.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOffer();
    }, []);

    // Handle Image Selection
    const handleImageChange = (e) => {
        const file = e.target.files?.[0] || null;
        setImage(file);
        if (file) {
            setPreview(URL.createObjectURL(file));
        }
        setErrors((prev) => ({ ...prev, image: "" }));
    };

    // Save or Update Offer Configuration
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErrors({});
        setMessage("");
        setError("");

        try {
            const formData = new FormData();
            formData.append("title", title);
            formData.append("desc", desc || "");
            formData.append("is_active", isActive ? "1" : "0");

            if (image) {
                formData.append("image", image);
            }

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/web-manager/offer`,
                formData,
                { headers: getAuthHeaders() }
            );

            setMessage(response.data?.message || "Offer configuration saved successfully!");
            setImage(null);
            await fetchOffer();
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                setError(err.response?.data?.message || "Something went wrong.");
            }
        } finally {
            setSaving(false);
        }
    };

    // Delete Offer Configuration
    const handleDelete = async () => {
        if (!currentOfferId) return;
        if (!window.confirm("Are you sure you want to delete this offer configuration?")) return;

        try {
            setError("");
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/web-manager/offer`,
                { headers: getAuthHeaders() }
            );

            setMessage("Offer deleted successfully.");
            setTitle("");
            setDesc("");
            setIsActive(true);
            setImage(null);
            setPreview(null);
            setCurrentOfferId(null);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to delete offer.");
        }
    };

    return (
        <PageLayout>
            <Container fluid className="py-4">
                <Row className="align-items-center mb-4">
                    <Col>
                        <h2 className="fw-bold mb-1">Offer Management</h2>
                        <p className="text-muted mb-0">
                            Configure active promotional banners and announcement offers.
                        </p>
                    </Col>
                    {currentOfferId && (
                        <Col xs="auto">
                            <Button
                                variant="danger"
                                className="px-4 fw-semibold"
                                onClick={handleDelete}
                            >
                                Clear Offer
                            </Button>
                        </Col>
                    )}
                </Row>

                {message && (
                    <Alert variant="success" dismissible onClose={() => setMessage("")}>
                        {message}
                    </Alert>
                )}

                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-4">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" />
                                <div className="mt-2 text-muted">Loading offer settings...</div>
                            </div>
                        ) : (
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={8}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold small text-muted text-uppercase">
                                                Offer Title <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="e.g. Summer Special 50% Off"
                                                value={title}
                                                onChange={(e) => {
                                                    setTitle(e.target.value);
                                                    setErrors((prev) => ({ ...prev, title: "" }));
                                                }}
                                                isInvalid={!!errors.title}
                                                className="bg-light border-0 py-2 px-3 text-dark"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {Array.isArray(errors.title) ? errors.title[0] : errors.title}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold small text-muted text-uppercase">
                                                Description
                                            </Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={4}
                                                placeholder="Enter details about this offer..."
                                                value={desc}
                                                onChange={(e) => {
                                                    setDesc(e.target.value);
                                                    setErrors((prev) => ({ ...prev, desc: "" }));
                                                }}
                                                isInvalid={!!errors.desc}
                                                className="bg-light border-0 py-2 px-3 text-dark"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {Array.isArray(errors.desc) ? errors.desc[0] : errors.desc}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold small text-muted text-uppercase me-3">
                                                Status
                                            </Form.Label>
                                            <div>
                                                <Form.Check
                                                    type="switch"
                                                    id="offer-active-switch"
                                                    label={
                                                        isActive ? (
                                                            <Badge bg="success">Active</Badge>
                                                        ) : (
                                                            <Badge bg="secondary">Inactive</Badge>
                                                        )
                                                    }
                                                    checked={isActive}
                                                    onChange={(e) => setIsActive(e.target.checked)}
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold small text-muted text-uppercase">
                                                Banner Image
                                            </Form.Label>
                                            <Form.Control
                                                type="file"
                                                accept=".jpg,.jpeg,.png,.webp"
                                                onChange={handleImageChange}
                                                isInvalid={!!errors.image}
                                                className="bg-light border-0 py-2 px-3 text-dark"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {Array.isArray(errors.image) ? errors.image[0] : errors.image}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        {preview ? (
                                            <div className="text-center p-3 bg-light rounded border mt-3">
                                                <Image
                                                    src={preview}
                                                    alt="Offer Preview"
                                                    fluid
                                                    rounded
                                                    style={{ maxHeight: "180px", objectFit: "contain" }}
                                                />
                                            </div>
                                        ) : (
                                            <div className="text-center p-4 bg-light rounded border border-dashed mt-3 text-muted">
                                                No image uploaded
                                            </div>
                                        )}
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-end mt-4">
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={saving}
                                        className="px-4 py-2 fw-semibold text-uppercase"
                                    >
                                        {saving ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Saving...
                                            </>
                                        ) : (
                                            "Save Offer Settings"
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </PageLayout>
    );
};

export default OffersPage;